import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useForm } from '@inertiajs/react'
import { CalendarDays, FileText, LoaderCircle, Paperclip, Trash2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import type { MemoryCopy } from './types'

interface Props {
  workerId: string
  copy: MemoryCopy
  initialMode?: AddMode
  onSubmitted: () => void
  onClose: () => void
}

type AddMode = 'text' | 'files'

export default function MemoryAddDialog({ workerId, copy, initialMode = 'text', onSubmitted, onClose }: Props) {
  const [mode, setMode] = useState<AddMode>(initialMode)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data, setData, post, processing } = useForm({
    memory: { content: '', context: '', event_date: '', files: [] as File[] },
  })

  const updateMemory = (next: Partial<typeof data.memory>) => setData('memory', { ...data.memory, ...next })
  const changeMode = (nextMode: AddMode) => {
    setMode(nextMode)
    if (nextMode === 'text') {
      updateMemory({ files: [] })
      if (fileInputRef.current) fileInputRef.current.value = ''
    } else {
      updateMemory({ content: '' })
    }
  }
  const submit = () => {
    post(`/dashboard/digital_workers/${workerId}/memories`, {
      forceFormData: mode === 'files',
      preserveScroll: true,
      onSuccess: () => {
        onSubmitted()
        onClose()
      },
    })
  }
  const canSubmit = mode === 'text' ? Boolean(data.memory.content.trim()) : data.memory.files.length > 0

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="flex max-h-[min(760px,calc(100vh-2rem))] max-w-2xl flex-col gap-0 overflow-hidden bg-card p-0 sm:rounded-xl [&>button]:hidden">
        <DialogHeader className="flex-row items-start gap-3 space-y-0 border-b border-border px-5 py-4 text-left">
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Upload className="size-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base">{copy.add.title}</DialogTitle>
            <DialogDescription className="mt-1 text-xs leading-5">{copy.add.description}</DialogDescription>
          </div>
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="icon-sm" aria-label={copy.detail.close}><X /></Button>
          </DialogClose>
        </DialogHeader>

        <div className="overflow-y-auto p-5">
          <Tabs value={mode} onValueChange={(value) => changeMode(value as AddMode)} className="mb-5">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="text"><FileText />{copy.add.text_tab}</TabsTrigger>
              <TabsTrigger value="files"><Paperclip />{copy.add.files_tab}</TabsTrigger>
            </TabsList>
          </Tabs>

          {mode === 'text' ? (
            <div className="space-y-2">
              <Label htmlFor="memory-content">{copy.add.content_label}</Label>
              <Textarea id="memory-content" autoFocus rows={8} maxLength={100000} value={data.memory.content} onChange={(event) => updateMemory({ content: event.target.value })} placeholder={copy.add.content_placeholder} className="min-h-52 resize-y leading-6" />
            </div>
          ) : (
            <div>
              <input ref={fileInputRef} type="file" multiple accept=".csv,.doc,.docx,.jpeg,.jpg,.md,.pdf,.png,.ppt,.pptx,.txt,.webp,.xls,.xlsx" onChange={(event) => updateMemory({ files: Array.from(event.target.files || []).slice(0, 3) })} className="sr-only" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="flex min-h-44 w-full flex-col items-center justify-center rounded-lg border border-dashed border-input bg-background p-6 text-center transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
                <Paperclip className="mb-3 size-6 text-primary/60" />
                <span className="text-sm font-semibold text-foreground">{copy.add.choose_files}</span>
                <span className="mt-1 max-w-sm text-xs leading-5 text-muted-foreground">{copy.add.files_hint}</span>
              </button>
              {data.memory.files.length > 0 && (
                <div className="mt-3 space-y-2" aria-label={copy.add.selected_files}>
                  {data.memory.files.map((file, index) => (
                    <div key={`${file.name}-${file.size}`} className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
                      <FileText className="size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate text-xs text-foreground">{file.name}</span>
                      <Button type="button" variant="ghost" size="icon-sm" onClick={() => updateMemory({ files: data.memory.files.filter((_, itemIndex) => itemIndex !== index) })} aria-label={`${copy.add.remove_file}: ${file.name}`} className="text-muted-foreground hover:text-destructive"><Trash2 /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <div className="space-y-2">
              <Label htmlFor="memory-context">{copy.add.context_label}</Label>
              <Input id="memory-context" type="text" maxLength={4000} value={data.memory.context} onChange={(event) => updateMemory({ context: event.target.value })} placeholder={copy.add.context_placeholder} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="memory-event-date">{copy.add.event_date_label}</Label>
              <span className="relative block">
                <CalendarDays className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="memory-event-date" type="date" value={data.memory.event_date} onChange={(event) => updateMemory({ event_date: event.target.value })} aria-describedby="memory-event-date-hint" className="pl-9" />
              </span>
              <span id="memory-event-date-hint" className="block text-[10.5px] leading-4 text-faint-foreground">{copy.add.event_date_hint}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border bg-muted/20 px-5 py-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={processing}>{copy.add.cancel}</Button>
          <Button type="button" onClick={submit} disabled={processing || !canSubmit}>
            {processing && <LoaderCircle className="animate-spin" />}
            {processing ? copy.add.processing : mode === 'text' ? copy.add.submit_text : copy.add.submit_files}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
